import { useEffect, useRef, useState, useCallback } from "react";
import * as Sentry from "@sentry/react";
import Icon from "./Icon";

export type ScannedFood = {
  name: string;
  kcal: number;
  protein: number;
  /** Carbs and fat were simply not read, so both rings under-counted silently. */
  carbs: number;
  fat: number;
  serving: string;
  /** Per-100g values so the serving-size dropdown works for scanned items too. */
  per100: { kcal: number; protein: number; carbs: number; fat: number } | null;
};

type ScanState = "requesting" | "scanning" | "fetching" | "not_found" | "error" | "denied";

async function lookupBarcode(barcode: string): Promise<ScannedFood> {
  const url = `https://world.openfoodfacts.org/api/v0/product/${barcode}.json`;
  const res = await fetch(url, {
    headers: { "User-Agent": "MoveNourishFlourish/1.0" },
  });
  if (!res.ok) throw new Error(`network: ${res.status} from ${url}`);

  // A 200 is not proof of JSON. OpenFoodFacts is a third party that can answer
  // with an HTML error page, a captcha, or a CDN interstitial, and res.json()
  // would then blow up with a bare SyntaxError that tells us nothing.
  const contentType = res.headers.get("content-type");
  if (!contentType || !contentType.toLowerCase().includes("application/json")) {
    throw new Error(
      `network: expected JSON from ${url} but got content-type ` +
        `"${contentType ?? "(none)"}" (HTTP ${res.status})`,
    );
  }

  const data = await res.json();
  if (data.status !== 1 || !data.product) throw new Error("not_found");

  const p = data.product;
  const name = (p.product_name || p.generic_name || "").trim() || "Unknown product";
  const servingQty = parseFloat(p.serving_quantity) || 100;
  // Carbs and fat come back in the SAME nutriments object this already reads.
  // They were never extracted, so a scanned item logged carbs: undefined and
  // fat: undefined and those rings quietly under-counted. Under-counting in
  // silence is worse than not tracking, because the number still looks true.
  const per100 = (k: string): number => Number(p.nutriments?.[`${k}_100g`] ?? 0);
  const atServing = (k: string): number =>
    Math.round(Number(p.nutriments?.[`${k}_serving`] ?? (per100(k) * servingQty) / 100));

  const kcalPer100 = p.nutriments?.["energy-kcal_100g"] ?? p.nutriments?.["energy_100g"] ?? 0;
  const kcal = Math.round(
    p.nutriments?.["energy-kcal_serving"] ?? (kcalPer100 * servingQty) / 100,
  );
  const protein = atServing("proteins");
  const carbs = atServing("carbohydrates");
  const fat = atServing("fat");
  const serving = p.serving_size || `${servingQty}g`;

  return {
    name,
    kcal,
    protein,
    carbs,
    fat,
    serving,
    // Lets the serving-size dropdown recompute a scanned item the same way it
    // does a searched one. Without this the only way to change the portion was
    // to hand-edit every macro box.
    per100: {
      kcal: Math.round(Number(kcalPer100)),
      protein: Math.round(per100("proteins")),
      carbs: Math.round(per100("carbohydrates")),
      fat: Math.round(per100("fat")),
    },
  };
}

export default function BarcodeScanner({
  onResult,
  onManual,
}: {
  onResult: (food: ScannedFood) => void;
  onManual: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const stopRef = useRef<(() => void) | null>(null);
  const [state, setState] = useState<ScanState>("requesting");
  const [errorMsg, setErrorMsg] = useState("");

  const handleBarcode = useCallback(
    async (barcode: string) => {
      setState("fetching");
      try {
        const food = await lookupBarcode(barcode);
        onResult(food);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "error";
        if (msg === "not_found") {
          // A real answer from the database, not a failure of ours — don't
          // page ourselves over a product nobody has catalogued yet.
          setErrorMsg(
            "We don't have that product in the food database yet. Enter the details by hand and you're good to go.",
          );
          setState("not_found");
        } else {
          console.error("[BarcodeScanner] barcode lookup failed", err);
          Sentry.captureException(err, { tags: { subsystem: "barcode-scanner" } });
          setErrorMsg(
            "The food database isn't answering right now. Nothing is wrong on your end — enter the details by hand and keep going.",
          );
          setState("error");
        }
        // Scanning already stopped when the barcode fired; release the handle.
        stopRef.current = null;
      }
    },
    [onResult],
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        if (cancelled || !videoRef.current) return;

        let fired = false;
        const controls = await (new BrowserMultiFormatReader()).decodeFromConstraints(
          { video: { facingMode: { ideal: "environment" } } },
          videoRef.current,
          (result) => {
            if (!result || fired || cancelled) return;
            fired = true;
            controls.stop();
            handleBarcode(result.getText());
          },
        );

        stopRef.current = () => controls.stop();
        if (!cancelled) setState("scanning");
      } catch (err: unknown) {
        if (cancelled) return;
        const name = err instanceof Error ? err.name : "";
        if (name === "NotAllowedError") {
          setState("denied");
        } else {
          setErrorMsg("Camera unavailable on this device. Enter details manually.");
          setState("error");
        }
      }
    })();

    return () => {
      cancelled = true;
      stopRef.current?.();
    };
  }, [handleBarcode]);

  const corner = (pos: Record<string, number | string>) => (
    <span
      style={{
        position: "absolute",
        width: 22,
        height: 22,
        ...pos,
      }}
    />
  );

  const borderColor = "#A8D4B8";
  const bw = "2.5px solid " + borderColor;

  return (
    <div className="flex flex-col items-center gap-3 py-1 mb-3">
      {/* Viewfinder */}
      <div
        style={{
          position: "relative",
          width: "100%",
          maxHeight: 220,
          aspectRatio: "4/3",
          borderRadius: 16,
          overflow: "hidden",
          background: "#0a0a0a",
        }}
      >
        <video
          ref={videoRef}
          playsInline
          muted
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />

        {/* Corner markers */}
        {(state === "scanning" || state === "requesting") && (
          <>
            <span style={{ position: "absolute", top: 14, left: 14, width: 22, height: 22, borderTop: bw, borderLeft: bw, borderRadius: "4px 0 0 0" }} />
            <span style={{ position: "absolute", top: 14, right: 14, width: 22, height: 22, borderTop: bw, borderRight: bw, borderRadius: "0 4px 0 0" }} />
            <span style={{ position: "absolute", bottom: 14, left: 14, width: 22, height: 22, borderBottom: bw, borderLeft: bw, borderRadius: "0 0 0 4px" }} />
            <span style={{ position: "absolute", bottom: 14, right: 14, width: 22, height: 22, borderBottom: bw, borderRight: bw, borderRadius: "0 0 4px 0" }} />
          </>
        )}

        {/* Overlay for requesting / fetching */}
        {(state === "requesting" || state === "fetching") && (
          <div
            style={{
              position: "absolute", inset: 0,
              background: "rgba(0,0,0,0.6)",
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: 10,
            }}
          >
            <Icon name="qr_code_scanner" size={34} color={borderColor} />
            <p style={{ color: "#F5F0E8", fontSize: 12.5, fontFamily: "inherit", textAlign: "center" }}>
              {state === "requesting" ? "Starting camera…" : "Looking up nutrition…"}
            </p>
          </div>
        )}
      </div>

      {/* Status messages */}
      {state === "scanning" && (
        <p className="mnf-body text-center" style={{ fontSize: 12.5, color: "var(--mnf-tg)" }}>
          Point at the barcode on the packaging
        </p>
      )}
      {(state === "error" || state === "not_found") && (
        <p className="mnf-body text-center" style={{ fontSize: 12.5, color: "var(--mnf-t)", lineHeight: 1.4 }}>
          {errorMsg}
        </p>
      )}
      {state === "denied" && (
        <p className="mnf-body text-center" style={{ fontSize: 12.5, color: "var(--mnf-t)", lineHeight: 1.4 }}>
          Camera access was denied.{"\n"}Allow it in browser settings, then try again.
        </p>
      )}

      <button
        type="button"
        onClick={onManual}
        className="mnf-body"
        style={{
          fontSize: 12, color: "var(--mnf-t)", background: "none",
          border: "none", cursor: "pointer", fontWeight: 600,
        }}
      >
        Enter manually instead
      </button>
    </div>
  );
}
